import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { FormattedNumberInput } from '@rd-web-markets/shared/dist/util';
import { useSelector } from 'react-redux';
import claimEmployeeRndApportionmentsService from '../../services/claim/claim_employee_rnd_apportionments.service';
import { useErrorHandling } from '@rd-web-markets/shared/dist/hooks/useErrorHandling';
import ApportionmentsList from '../ApportionmentsList';
import dataLicencesCostsService from '../../services/claim/data_licences_costs.service';
import ChangeHighlightedImmutableUnderReviewControl from '@rd-web-markets/shared/dist/util/reviewEditControls/ChangeHighlightedImmutableUnderReviewControl';
import { DeleteButton } from '@rd-web-markets/shared/dist/util/buttons';
export function DataLicencesCostsRow(_ref) {
  let {
    sortedClaimProjectReports,
    index,
    claim,
    handleDelete,
    dataLicencesCosts,
    setDataLicencesCosts,
    refreshClaim,
    totalPayrollCostInvalid,
    handleError,
    headerScrollPosition,
    isShowInvolvement
  } = _ref;
  const [hasError, setHasError] = useState(false);
  const claimEmployeeRndApportRef = useRef(null);
  const user = useSelector(state => state.auth.user);
  useEffect(() => {
    if (claimEmployeeRndApportRef.current) {
      claimEmployeeRndApportRef.current.scrollTo({
        left: headerScrollPosition
      });
    }
  }, [claimEmployeeRndApportRef, headerScrollPosition]);
  const dataLicencesCost = useMemo(() => {
    return {
      ...dataLicencesCosts[index]
    };
  }, [dataLicencesCosts, index]);
  const dispatch = useDispatch();
  const updateDataLicencesCost = useCallback(dataLicencesCost => {
    const newDataLicencesCosts = [...dataLicencesCosts];
    newDataLicencesCosts[index] = {
      ...dataLicencesCost
    };
    setDataLicencesCosts([...newDataLicencesCosts]);
  }, [dataLicencesCosts, index, setDataLicencesCosts]);
  const handleApportionmentChange = useErrorHandling(useCallback(async (dataLicencesCost, apportionment) => {
    const oldApportionment = dataLicencesCost.claim_employee_rnd_apportionments.find(a => a.claim_project_report_id === apportionment.claim_project_report_id);
    const index = dataLicencesCost.claim_employee_rnd_apportionments.indexOf(oldApportionment);
    const newApportionments = [...dataLicencesCost.claim_employee_rnd_apportionments];
    if (index === -1) {
      newApportionments.push(apportionment);
    } else {
      newApportionments[index] = {
        ...apportionment
      };
    }
    const updatedDataLicencesCost = await dataLicencesCostsService.get(dataLicencesCost);
    updatedDataLicencesCost.claim_employee_rnd_apportionments = newApportionments;
    updateDataLicencesCost(updatedDataLicencesCost);
  }, [updateDataLicencesCost]));
  const handleChange = useCallback(event => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    dataLicencesCost[event.target.name] = value;
    updateDataLicencesCost(dataLicencesCost);
  }, [updateDataLicencesCost, dataLicencesCost]);
  const saveChange = useCallback(() => {
    let method;
    if (dataLicencesCost.id) {
      method = 'update';
    } else {
      method = 'create';
    }
    dataLicencesCostsService[method](dataLicencesCost).then(updatedDataLicencesCost => {
      setHasError(false);
      updateDataLicencesCost(updatedDataLicencesCost);
      refreshClaim();
    }).catch(e => {
      setHasError(true);
      dispatch(handleError(e));
    });
  }, [updateDataLicencesCost, dataLicencesCost, dispatch, refreshClaim]);
  const hasPermissions = () => {
    return user.account_type !== 'customer' && user.account_type !== 'accountant';
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("tr", {
    className: hasError ? 'bg-warning' : ''
  }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: Form.Control,
    onChange: handleChange,
    onBlur: saveChange,
    disabled: claim.financials_locked || !hasPermissions(),
    name: "name",
    object: {
      ...dataLicencesCost,
      claim: {
        ...claim,
        ...dataLicencesCost.claim
      }
    },
    size: "sm",
    type: "text"
  })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: FormattedNumberInput,
    onChange: handleChange,
    onBlur: saveChange,
    disabled: claim.financials_locked || !hasPermissions(),
    name: "net_amount",
    object: {
      ...dataLicencesCost,
      claim: {
        ...claim,
        ...dataLicencesCost.claim
      }
    },
    size: "sm",
    type: "text"
  })), isShowInvolvement && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: Form.Control,
    onChange: handleChange,
    onBlur: saveChange,
    object: {
      ...dataLicencesCost,
      claim: {
        ...claim,
        ...dataLicencesCost.claim
      }
    },
    disabled: claim.financials_locked || !hasPermissions(),
    name: "direct_rnd_apportionment",
    type: "text",
    size: "sm"
  })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: Form.Control,
    onChange: handleChange,
    onBlur: saveChange,
    object: {
      ...dataLicencesCost,
      claim: {
        ...claim,
        ...dataLicencesCost.claim
      }
    },
    disabled: claim.financials_locked || !hasPermissions(),
    name: "indirect_rnd_apportionment",
    type: "text",
    size: "sm"
  })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: FormattedNumberInput,
    decimalScale: 1,
    onChange: handleChange,
    onBlur: saveChange,
    disabled: !hasPermissions() || claim.claim_group.project_methodology === 'project_basis',
    name: "rnd_apportionment",
    object: {
      ...dataLicencesCost,
      claim: {
        ...claim,
        ...dataLicencesCost.claim
      }
    },
    size: "sm",
    type: "text"
  }))), claim.claim_group.project_methodology === 'project_basis' && /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ApportionmentsList, {
    claim: claim,
    sortedClaimProjectReports: sortedClaimProjectReports,
    headerScrollPosition: headerScrollPosition,
    claimEmployeeRndApportRef: claimEmployeeRndApportRef,
    handleApportionmentChange: handleApportionmentChange,
    claimEmployeeRndApportionmentsService: claimEmployeeRndApportionmentsService,
    model: dataLicencesCost,
    apportionmentable_type: "UkSection::DataLicencesCost",
    isShowInvolvement: isShowInvolvement
  })), !isShowInvolvement && /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: FormattedNumberInput,
    decimalScale: 1,
    onChange: handleChange,
    onBlur: saveChange,
    disabled: !hasPermissions() || claim.claim_group.project_methodology === 'project_basis',
    name: "rnd_apportionment",
    object: {
      ...dataLicencesCost,
      claim: {
        ...claim,
        ...dataLicencesCost.claim
      }
    },
    size: "sm",
    type: "text"
  })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: FormattedNumberInput,
    disabled: true || !hasPermissions(),
    name: "rnd_amount",
    object: {
      ...dataLicencesCost,
      claim: {
        ...claim,
        ...dataLicencesCost.claim
      }
    },
    size: "sm",
    type: "text"
  })), /*#__PURE__*/React.createElement("td", null, hasPermissions() && /*#__PURE__*/React.createElement(DeleteButton, {
    style: {
      height: '28px'
    },
    iconClassName: "text-danger fa-2x",
    disabled: claim.is_locked || claim.financials_locked || claim.active_change_set,
    isImmutableUnderReview: true,
    onClick: handleDelete
  }))));
}